import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchCollection } from '../lib/cms';
import { getOptimizedImageUrl } from '../lib/images';

const PLATFORM_DATA = {
  instagram: {
    name: 'Instagram',
    badge: 'Instagram Reel',
    views: '👁 850.000 afspilninger',
    sound: '🎵 Original sundhedsfaglig formidling & speak • Health4media',
    tag: '#sundhedsfaglig #klinikliv #fysioterapi #health4media',
    likes: '48.9K',
    comments: '512',
    shares: '850',
    saves: '2.1K',
    activeClass: 'bg-gradient-to-tr from-amber-500 via-pink-500 to-purple-600 text-white border-transparent shadow-md'
  },
  tiktok: {
    name: 'TikTok',
    badge: 'TikTok Trending',
    views: '👁 1.2M visninger',
    sound: '🎵 Original sound • Dansk Sundhedsformidling',
    tag: '#tiktokhealth #klinik #fysioterapi #health4media',
    likes: '84.2K',
    comments: '1.4K',
    shares: '3.2K',
    saves: '5.9K',
    activeClass: 'bg-slate-900 text-white border-slate-900 shadow-md'
  },
  facebook: {
    name: 'Facebook',
    badge: 'Facebook Watch',
    views: '👁 420.000 visninger',
    sound: '🎵 Faglig viden og patientinformation • Health4media',
    tag: '#patienttryghed #behandling #sundhedidanmark #health4media',
    likes: '19.4K',
    comments: '340',
    shares: '620',
    saves: '890',
    activeClass: 'bg-[#1877F2] text-white border-[#1877F2] shadow-md'
  },
  x: {
    name: 'X',
    badge: 'X Video',
    views: '👁 195.000 visninger',
    sound: '🎵 Fagligt indblik & sundhedskommunikation',
    tag: '#sundhedsfaglig #klinikviden #health4media',
    likes: '8.7K',
    comments: '185',
    shares: '410',
    saves: '320',
    activeClass: 'bg-black text-white border-black shadow-md'
  }
};


export default function HomePage() {
  const [cases, setCases] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isReelLiked, setIsReelLiked] = useState(false);
  const [isReelBookmarked, setIsReelBookmarked] = useState(false);
  const [activePlatform, setActivePlatform] = useState('instagram');

  useEffect(() => {
    document.title = 'Health4media.dk';
    let isMounted = true;

    async function loadData() {
      try {
        const [casesData, servicesData] = await Promise.all([
          fetchCollection('cases'),
          fetchCollection('services')
        ]);
        if (isMounted) {
          setCases(casesData || []);
          setServices(servicesData || []);
        }
      } catch (e) {
        console.error('Error loading home data:', e);
      } finally {
        if (isMounted) setLoading(false);
      }
    }

    loadData();
    return () => { isMounted = false; };
  }, []);

  return (
    <div className="space-y-0">
      
      {/* HERO SECTION */}
      <section className="relative overflow-hidden bg-slate-950 text-white min-h-[420px] sm:min-h-[520px] lg:min-h-[640px] flex items-center">
        {/* Full-width background image with cinematic gradient overlay */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img
            src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/48b457c4-b6b2-4d62-b92b-6229aa267ddf/hero-modern-healthcare-consultation.png', { width: 1920, quality: 85 })}
            alt="Tryg og autentisk sundhedskonsultation i moderne dansk klinik"
            className="w-full h-full object-cover object-center animate-hero-zoom"
          />
          {/* Gradient overlay: dark blue opacity on left, transparent on right */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#061833]/95 via-[#0c254b]/80 via-40% to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#061833]/80 via-transparent to-[#061833]/20" />
        </div>
        
        <div className="relative z-10 w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20 lg:py-28">
          <div className="max-w-3xl space-y-4 sm:space-y-6">
            <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-white leading-[1.1]">
              Content, der skaber <br />
              tillid, synlighed &amp; tryghed
            </h1>

            <p className="text-base sm:text-lg lg:text-xl text-white leading-relaxed font-normal max-w-2xl">
              Mennesker vælger de behandlere, de føler sig trygge ved. Vi hjælper sundhedsklinikker med autentisk videoproduktion og målrettet formidling, der fjerner usikkerhed, opbygger varig tillid og tiltrækker nye patienter.
            </p>

            {/* Primary Action Button */}
            <div className="flex flex-wrap items-center gap-4 pt-1 sm:pt-2">
              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2.5 px-6 py-3 sm:px-8 sm:py-4 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-sm sm:text-base transition-all duration-200 shadow-xl shadow-sky-950/50 hover:shadow-2xl hover:-translate-y-0.5"
              >
                <span>Book en samtale</span>
                <i className="fa-solid fa-arrow-right text-xs sm:text-sm" aria-hidden="true" />
              </Link>
            </div>

            {/* Clean Key Points */}
            <div className="pt-3 sm:pt-6 flex flex-wrap items-center gap-y-2 gap-x-6 text-xs sm:text-sm text-white font-medium">
              <span className="flex items-center gap-2">
                <i className="fa-solid fa-check text-sky-400" aria-hidden="true" />
                <span>Opbyg varig patienttillid & tryghed</span>
              </span>
              <span className="flex items-center gap-2">
                <i className="fa-solid fa-check text-sky-400" aria-hidden="true" />
                <span>Maksimal lokal synlighed & flere bookinger</span>
              </span>
            </div>

          </div>
        </div>
      </section>

      {/* 3 CORE USPs (High Visual Priority) */}
      <section className="py-16 sm:py-24 bg-white border-b border-slate-200/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-3xl mx-auto text-center mb-14">
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
              Tre afgørende fordele for jeres praksis
            </h2>
            <p className="text-base sm:text-lg text-slate-600 mt-3">
              Sundhed handler om tryghed. Autentisk visuel formidling og professionel videoproduktion fjerner usikkerhed og bygger relationer hurtigere end traditionel tekst.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            
            {/* Benefit 1: Mere tillid */}
            <div className="bg-white rounded-2xl p-8 border border-slate-200/80 shadow-xs flex flex-col justify-between md:col-span-1 lg:col-span-1">
              <div>
                <div className="text-3xl text-sky-600 mb-5">
                  <i className="fa-solid fa-heart-pulse" aria-hidden="true" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  Mere tillid
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                  Professionel og nærværende videokommunikation nedbryder frygt og opbygger stærk personlig tillid før første konsultation.
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-slate-100 text-xs font-semibold text-sky-800 flex items-center gap-2">
                <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                <span>Skaber personlig genkendelighed</span>
              </div>
            </div>

            {/* Benefit 2: Større synlighed */}
            <div className="bg-white rounded-2xl p-8 border border-slate-200/80 shadow-xs flex flex-col justify-between md:col-span-1 lg:col-span-1">
              <div>
                <div className="text-3xl text-sky-600 mb-5">
                  <i className="fa-solid fa-chart-line" aria-hidden="true" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  Større synlighed
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                  Opnå markant højere organisk rækkevidde i jeres lokalområde end med traditionelle opslag.
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-slate-100 text-xs font-semibold text-sky-800 flex items-center gap-2">
                <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                <span>Maksimal lokal gennemslagskraft</span>
              </div>
            </div>

            {/* Benefit 3: Flere patienter */}
            <div className="bg-white rounded-2xl p-8 border border-slate-200/80 shadow-xs flex flex-col justify-between md:col-span-2 lg:col-span-1">
              <div>
                <div className="text-3xl text-sky-600 mb-5">
                  <i className="fa-solid fa-user-plus" aria-hidden="true" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-3">
                  Flere patienter
                </h3>
                <p className="text-slate-600 leading-relaxed text-sm sm:text-base">
                  Tiltræk velinformerede og trygge patienter, der er klar til at starte et forløb hos jer.
                </p>
              </div>
              <div className="mt-6 pt-4 border-t border-slate-100 text-xs font-semibold text-sky-800 flex items-center gap-2">
                <i className="fa-solid fa-check text-sky-600" aria-hidden="true" />
                <span>Kvalificerede henvendelser</span>
              </div>
            </div>

          </div>

          <div className="mt-12 text-center">
            <Link
              to="/kontakt"
              className="inline-flex items-center gap-2 text-slate-900 hover:text-sky-700 font-semibold transition text-base"
            >
              <span>Book en samtale om jeres muligheder</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

        </div>
      </section>

      {/* TARGET AUDIENCE SECTION */}
      <section className="py-16 sm:py-20 bg-slate-50 border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="max-w-5xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10 items-center">
            
            {/* Left side: Mobil visning med video mockup + SoMe boks */}
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              {/* Ensartet samlet kolonne med fast bredde, så telefon og SoMe-boks flugter snorlige i bredden */}
              <div className="w-full max-w-[310px] sm:max-w-[340px] flex flex-col items-center space-y-3.5">
                
                {/* Mobil mock-up - Nordic minimalistisk æstetik, ultra-thin bezel, elegante kurver, realistisk glasreflektion & studio drop shadow */}
                <div className="w-full relative">
                  {/* Blød studio drop shadow under telefonen */}
                  <div className="absolute -bottom-5 left-1/2 -translate-x-1/2 w-[85%] h-8 bg-slate-900/40 blur-xl rounded-full pointer-events-none" />

                  {/* Ydre chassis-detaljer (minimalistiske knapper) */}
                  <div className="absolute -left-[2.5px] top-[95px] w-[2.5px] h-6 bg-[#2a2d33] rounded-l-xs shadow-xs z-0" />
                  <div className="absolute -left-[2.5px] top-[135px] w-[2.5px] h-11 bg-[#2a2d33] rounded-l-xs shadow-xs z-0" />
                  <div className="absolute -left-[2.5px] top-[190px] w-[2.5px] h-11 bg-[#2a2d33] rounded-l-xs shadow-xs z-0" />
                  <div className="absolute -right-[2.5px] top-[140px] w-[2.5px] h-14 bg-[#2a2d33] rounded-r-xs shadow-xs z-0" />

                  {/* Smartphone chassis med ultra-thin bezel, elegant curved corners og metallic finish */}
                  <div className="relative w-full aspect-[9/19] rounded-[2.75rem] sm:rounded-[3rem] p-[5px] sm:p-[6px] bg-gradient-to-b from-[#383b42] via-[#1c1e23] to-[#0d0e11] ring-1 ring-white/20 shadow-[0_25px_60px_-15px_rgba(0,0,0,0.65),0_0_0_1px_rgba(255,255,255,0.08)] select-none">
                    {/* Indre skærm med ultra-tynd sort bezel */}
                    <div className="relative w-full h-full rounded-[2.45rem] sm:rounded-[2.7rem] bg-black overflow-hidden ring-1 ring-black/90">
                      
                      {/* Dynamic Island svævende fra toppen */}
                      <div className="absolute top-2.5 left-1/2 -translate-x-1/2 w-24 sm:w-26 h-5.5 bg-black rounded-full z-40 flex items-center justify-between px-2.5 shadow-md border border-white/10">
                        {/* Frontkamera linse med optisk dybde */}
                        <span className="w-2.5 h-2.5 rounded-full bg-[#070b10] ring-1 ring-slate-800 flex items-center justify-center">
                          <span className="w-1 h-1 rounded-full bg-blue-950" />
                        </span>
                        {/* FaceID / TrueDepth sensor */}
                        <span className="w-2 h-2 rounded-full bg-[#0c0f14]" />
                      </div>

                      {/* Skærm-højttaler øverst i glasset */}
                      <div className="absolute top-1 left-1/2 -translate-x-1/2 w-9 h-0.5 bg-slate-700/70 rounded-full z-30" />

                      {/* Screen image: Sundhedsfaglig formidling i klinikken */}
                      <img
                        src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/48b457c4-b6b2-4d62-b92b-6229aa267ddf/healthcare-clinic-reel-screen.png', { width: 720, quality: 90 })}
                        alt="Sundhedsfaglig formidling og rådgivning i en lys, moderne dansk klinik"
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />

                      {/* Realistisk diagonal glasreflektion */}
                      <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/[0.08] to-transparent pointer-events-none z-15" />

                      {/* Semi-transparent gradient overlay for optimal læsbarhed */}
                      <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-transparent via-35% to-black/90 pointer-events-none z-10" />

                      {/* Top left: small circular profile badge with account name "@sundhedsklinik" and a bold text pill badge saying "850.000 afspilninger" */}
                      <div className="absolute top-10 left-3 sm:left-4 z-20 flex flex-col gap-1.5 items-start">
                        {/* Profile badge with account name */}
                        <div className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-black/60 backdrop-blur-md border border-white/20 text-white shadow-lg">
                          <div className="w-4 h-4 rounded-full bg-gradient-to-tr from-cyan-600 via-teal-600 to-slate-800 ring-1 ring-white/90 overflow-hidden flex items-center justify-center text-[8px] font-bold text-white shrink-0">
                            <i className="fa-solid fa-stethoscope text-[7px]" />
                          </div>
                          <span className="text-[10px] sm:text-[10.5px] font-bold tracking-tight text-white">@sundhedsklinik</span>
                        </div>

                        {/* Bold text pill badge */}
                        <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-rose-600 text-white text-[10.5px] sm:text-[11px] font-bold tracking-wide shadow-lg backdrop-blur-xs">
                          <span className="w-1.5 h-1.5 rounded-full bg-white animate-pulse" />
                          <span>850.000 afspilninger</span>
                        </div>
                      </div>

                      {/* Right side vertical interaction column: clean white icon buttons with counters */}
                      <div className="absolute right-2.5 sm:right-3 bottom-18 z-20 flex flex-col items-center gap-3 sm:gap-3.5 text-white">
                        {/* Heart icon with 48.9K */}
                        <button
                          type="button"
                          onClick={() => setIsReelLiked(!isReelLiked)}
                          className="flex flex-col items-center group transition active:scale-90 focus:outline-hidden cursor-pointer"
                          title="Synes godt om"
                        >
                          <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center backdrop-blur-md transition ${isReelLiked ? 'bg-rose-500 text-white' : 'bg-black/50 text-white group-hover:text-rose-400 border border-white/10'}`}>
                            <i className={`fa-solid fa-heart text-sm sm:text-base transition-transform ${isReelLiked ? 'scale-115 text-white' : 'text-white'}`} />
                          </div>
                          <span className="text-[10px] sm:text-[11px] font-bold mt-0.5 drop-shadow text-white">
                            {isReelLiked ? '49.0K' : '48.9K'}
                          </span>
                        </button>

                        {/* Comment speech bubble with 512 */}
                        <div className="flex flex-col items-center">
                          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-black/50 backdrop-blur-md border border-white/10 flex items-center justify-center text-white hover:text-sky-300 transition">
                            <i className="fa-solid fa-comment text-sm sm:text-base text-white" />
                          </div>
                          <span className="text-[10px] sm:text-[11px] font-bold mt-0.5 drop-shadow text-white">
                            512
                          </span>
                        </div>

                        {/* Share bookmark/arrow with 850.2K */}
                        <div className="flex flex-col items-center">
                          <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-black/50 backdrop-blur-md border border-white/10 flex items-center justify-center text-white hover:text-emerald-300 transition">
                            <i className="fa-solid fa-share text-sm sm:text-base text-white" />
                          </div>
                          <span className="text-[10px] sm:text-[11px] font-bold mt-0.5 drop-shadow text-white">
                            850.2K
                          </span>
                        </div>

                        {/* Bogmærke */}
                        <button
                          type="button"
                          onClick={() => setIsReelBookmarked(!isReelBookmarked)}
                          className="flex flex-col items-center group transition active:scale-90 focus:outline-hidden cursor-pointer"
                          title="Gem video"
                        >
                          <div className={`w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center backdrop-blur-md transition ${isReelBookmarked ? 'bg-amber-500 text-white' : 'bg-black/50 text-white group-hover:text-amber-300 border border-white/10'}`}>
                            <i className="fa-solid fa-bookmark text-sm sm:text-base text-white" />
                          </div>
                          <span className="text-[10px] sm:text-[11px] font-bold mt-0.5 drop-shadow text-white">
                            {isReelBookmarked ? '5.4K' : '2.1K'}
                          </span>
                        </button>
                      </div>

                      {/* Bottom overlay: semi-transparent gradient with bold white caption and spinning sound disc */}
                      <div className="absolute bottom-4 left-3 sm:left-4 right-14 sm:right-16 z-20 text-white space-y-1.5">
                        {/* Profilbadge */}
                        <div className="flex items-center gap-1.5">
                          <div className="w-6 h-6 rounded-full bg-gradient-to-tr from-cyan-600 to-teal-500 ring-1.5 ring-white/90 overflow-hidden shrink-0 flex items-center justify-center text-[9px] font-bold text-white shadow-sm">
                            <i className="fa-solid fa-user-doctor text-[9px]" />
                          </div>
                          <div className="flex items-center gap-1 min-w-0">
                            <span className="font-bold text-xs sm:text-[13px] tracking-tight drop-shadow truncate">
                              Sundhed & Fysioterapi
                            </span>
                            <i className="fa-solid fa-circle-check text-sky-400 text-xs shrink-0" aria-hidden="true" />
                          </div>
                        </div>

                        {/* Bold hvid caption */}
                        <p className="text-[11px] sm:text-xs text-white font-bold leading-snug drop-shadow">
                          Ondt i lænden når du sidder på kontoret? Her er 3 enkle øvelser, du kan lave direkte ved skrivebordet uden udstyr 🩺💡 Se med til slut!{' '}
                          <span className="text-cyan-300 font-semibold">#fysioterapi #rygsmerter #kontorliv #sundhedsfaglig #behandling</span>
                        </p>

                        {/* Spinning sound disc icon labeled "Original sundhedsfaglig formidling & speak • Health4media" */}
                        <div className="flex items-center gap-1.5 text-[10px] sm:text-[10.5px] text-white/95 font-medium overflow-hidden whitespace-nowrap drop-shadow pt-0.5">
                          <i className="fa-solid fa-compact-disc text-cyan-300 text-[11px] shrink-0 animate-spin" style={{ animationDuration: '3.5s' }} />
                          <span className="truncate">Original sundhedsfaglig formidling & speak • Health4media</span>
                        </div>
                      </div>

                      {/* iPhone Home Indicator bar nederst */}
                      <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 w-28 h-1 bg-white/70 rounded-full z-30" />
                    </div>
                  </div>
                </div>

                {/* SoMe boks nedenunder - perfekt flugtende med telefonens bredde */}
                <div className="w-full bg-white rounded-2xl p-3.5 sm:p-4 shadow-lg border border-slate-200/90 transition">
                  {/* Header med grøn pulserende indikator & orange badge */}
                  <div className="flex items-center justify-between gap-2 pb-2 border-b border-slate-100">
                    <div className="flex items-center gap-2">
                      <span className="relative flex h-2 w-2">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                        <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
                      </span>
                      <span className="font-bold text-slate-900 text-xs tracking-tight">
                        Vi hjælper jer på:
                      </span>
                    </div>
                    <span className="inline-flex items-center px-1.5 py-0.5 rounded-full text-[10px] font-semibold bg-amber-50 text-amber-700 border border-amber-200/80 shrink-0">
                      + flere
                    </span>
                  </div>

                  {/* 4-kolonners platformskort med interaktiv integration til telefonen */}
                  <div className="grid grid-cols-4 gap-1.5 mt-2.5 touch-manipulation">
                    {/* TikTok */}
                    <button
                      type="button"
                      onClick={() => setActivePlatform('tiktok')}
                      aria-label="TikTok"
                      className={`group flex flex-col items-center justify-center py-2 px-1 rounded-xl border text-slate-700 transition-all duration-200 cursor-pointer ${
                        activePlatform === 'tiktok'
                          ? 'bg-slate-900 text-white border-slate-900 shadow-md ring-2 ring-slate-900/20'
                          : 'bg-slate-50 border-slate-200/80 hover:bg-slate-900 hover:text-white hover:border-slate-900 hover:shadow-xs'
                      }`}
                    >
                      <i className="fa-brands fa-tiktok text-base sm:text-lg mb-0.5 transition-transform group-hover:scale-110" aria-hidden="true" />
                      <span className="text-[9.5px] sm:text-[10.5px] font-semibold tracking-tight">TikTok</span>
                    </button>

                    {/* Instagram */}
                    <button
                      type="button"
                      onClick={() => setActivePlatform('instagram')}
                      aria-label="Instagram"
                      className={`group flex flex-col items-center justify-center py-2 px-1 rounded-xl border text-slate-700 transition-all duration-200 cursor-pointer ${
                        activePlatform === 'instagram'
                          ? 'bg-gradient-to-tr from-amber-500 via-pink-500 to-purple-600 text-white border-transparent shadow-md ring-2 ring-pink-500/20'
                          : 'bg-slate-50 border-slate-200/80 hover:bg-gradient-to-tr hover:from-amber-500 hover:via-pink-500 hover:to-purple-600 hover:text-white hover:border-transparent hover:shadow-xs'
                      }`}
                    >
                      <i className="fa-brands fa-instagram text-base sm:text-lg mb-0.5 transition-transform group-hover:scale-110" aria-hidden="true" />
                      <span className="text-[9.5px] sm:text-[10.5px] font-semibold tracking-tight">Instagram</span>
                    </button>

                    {/* Facebook */}
                    <button
                      type="button"
                      onClick={() => setActivePlatform('facebook')}
                      aria-label="Facebook"
                      className={`group flex flex-col items-center justify-center py-2 px-1 rounded-xl border text-slate-700 transition-all duration-200 cursor-pointer ${
                        activePlatform === 'facebook'
                          ? 'bg-[#1877F2] text-white border-[#1877F2] shadow-md ring-2 ring-[#1877F2]/20'
                          : 'bg-slate-50 border-slate-200/80 hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2] hover:shadow-xs'
                      }`}
                    >
                      <i className="fa-brands fa-facebook-f text-base sm:text-lg mb-0.5 transition-transform group-hover:scale-110" aria-hidden="true" />
                      <span className="text-[9.5px] sm:text-[10.5px] font-semibold tracking-tight">Facebook</span>
                    </button>

                    {/* X (Twitter) */}
                    <button
                      type="button"
                      onClick={() => setActivePlatform('x')}
                      aria-label="X"
                      className={`group flex flex-col items-center justify-center py-2 px-1 rounded-xl border text-slate-700 transition-all duration-200 cursor-pointer ${
                        activePlatform === 'x'
                          ? 'bg-black text-white border-black shadow-md ring-2 ring-black/20'
                          : 'bg-slate-50 border-slate-200/80 hover:bg-black hover:text-white hover:border-black hover:shadow-xs'
                      }`}
                    >
                      <i className="fa-brands fa-x-twitter text-base sm:text-lg mb-0.5 transition-transform group-hover:scale-110" aria-hidden="true" />
                      <span className="text-[9.5px] sm:text-[10.5px] font-semibold tracking-tight">X</span>
                    </button>
                  </div>
                </div>

              </div>
            </div>

            {/* Right side: Header & Audience cards */}
            <div className="lg:col-span-7 space-y-6 lg:pl-4">
              <div>
                <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900">
                  Skræddersyet til hele den danske sundhedsbranche
                </h2>
                <p className="text-slate-600 mt-3 text-sm sm:text-base leading-relaxed">
                  Uanset klinikkens speciale tilpasser vi formaterne til jeres faglige kontekst, patientgruppe og kliniske standarder.
                </p>
              </div>

              <ul className="space-y-3 pt-2">
                <li>
                  <h3 className="font-semibold text-slate-900 text-base">Hospitaler & Privathospitaler</h3>
                </li>

                <li>
                  <h3 className="font-semibold text-slate-900 text-base">Kiropraktorer & Behandlere</h3>
                </li>

                <li>
                  <h3 className="font-semibold text-slate-900 text-base">Fysioterapi & Træning</h3>
                </li>

                <li>
                  <h3 className="font-semibold text-slate-900 text-base">Speciallæger & Tandlæger</h3>
                </li>

                <li>
                  <h3 className="font-semibold text-sky-600 text-base">– og meget mere</h3>
                </li>
              </ul>

              <div className="pt-3">
                <Link
                  to="/kontakt"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-slate-900 text-white hover:bg-sky-700 font-semibold transition text-sm"
                >
                  <span>Hør om mulighederne for jeres praksis</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* CORE SERVICES SECTION (Clear multi-service positioning with broad spectrum) */}
      <section className="py-16 sm:py-24 bg-white border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="mb-12">
            <div className="max-w-3xl">
              <span className="text-xs font-bold text-sky-700 uppercase tracking-wider block mb-2">
                En bred vifte af ydelser
              </span>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                En samlet vifte af services til klinikkens synlighed
              </h2>
              <p className="text-base sm:text-lg text-slate-600 mt-3">
                Vi dækker hele paletten inden for visuel sundhedsformidling: Fra personlig optagevejledning og fotovejlending til professionel redigering, komplet SoMe-opsætning og løbende drift af jeres Meta postkalender.
              </p>
            </div>
          </div>

          {/* 3 Main Service Pillars */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            
            {/* Service 1: Online Vejledning */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200 shadow-xs flex flex-col justify-between hover:shadow-md transition-shadow group">
              <div>
                <div className="text-3xl text-sky-600 mb-5">
                  <i className="fa-solid fa-mobile-screen-button" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  Optagelse, Billeder & Emnesparring
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6">
                  Opbyg umiddelbar patienttillid og tryghed. Vi vejleder jer online i vinkler, belysning, lyd og udvælgelse af faglige emner, der afliver myter og fjerner tvivl inden første henvendelse.
                </p>
              </div>
              <div className="pt-4 border-t border-slate-200">
                <Link
                  to="/ydelser/klinisk-videoproduktion"
                  className="inline-flex items-center gap-2 text-xs font-bold text-sky-700 hover:text-sky-800"
                >
                  <span>Læs om optage- og fotovejledning</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Service 2: Videoredigering & Content */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200 shadow-xs flex flex-col justify-between hover:shadow-md transition-shadow group">
              <div>
                <div className="text-3xl text-sky-600 mb-5">
                  <i className="fa-solid fa-video" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  Videoredigering & Content-opsætning
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6">
                  Omsæt visninger til reelle bookinger. Vi klipper skarpe 30–90 sek. videoer med fængende hooks, undertekster og klinikgrafik, der fastholder interessen og guider patienten til at vælge jer.
                </p>
              </div>
              <div className="pt-4 border-t border-slate-200">
                <Link
                  to="/ydelser/shortform-content"
                  className="inline-flex items-center gap-2 text-xs font-bold text-sky-700 hover:text-sky-800"
                >
                  <span>Læs om redigering & content</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

            {/* Service 3: Meta Postkalender & Profilopsætning */}
            <div className="bg-slate-50 rounded-2xl p-8 border border-slate-200 shadow-xs flex flex-col justify-between hover:shadow-md transition-shadow group">
              <div>
                <div className="text-3xl text-sky-600 mb-5">
                  <i className="fa-solid fa-calendar-days" aria-hidden="true" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-3">
                  Meta Postkalender & SoMe-profiler
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6">
                  Sikr kontinuerlig synlighed i lokalområdet. Vi opsætter professionelle profiler og driver en fast udgivelsesrytme, så jeres praksis altid er synlig, når patienternes behandlingsbehov opstår.
                </p>
              </div>
              <div className="pt-4 border-t border-slate-200">
                <Link
                  to="/ydelser/distribution-og-synlighed"
                  className="inline-flex items-center gap-2 text-xs font-bold text-sky-700 hover:text-sky-800"
                >
                  <span>Læs om distribution & kalender</span>
                  <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
                </Link>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* WIDE SPECTRUM BREAKDOWN SECTION (REPLACED PARTNERSHIP SECTION) */}
      <section className="py-16 sm:py-20 bg-[#10172c] text-white border-b border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mb-8">
            <h2 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight">
              Vores brede vifte af ydelser omfatter bl.a.:
            </h2>
            <p className="text-sm sm:text-base text-slate-300 mt-2">
              Klinikker har forskellige udgangspunkter – derfor kan I frit kombinere og tilpasse de services, der passer til netop jeres behov:
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
            <div className="bg-slate-900/80 p-5 rounded-xl border border-slate-800/80 shadow-xs hover:border-slate-700 transition-colors">
              <div className="text-sky-400 text-xl mb-2.5">
                <i className="fa-solid fa-mobile-screen" aria-hidden="true" />
              </div>
              <h3 className="text-sm font-bold text-white mb-1">Optagelsesvejledning</h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                Konkrete råd til lys, ro og lyd, så I fremstår rolige, kompetente og tillidsvækkende på video.
              </p>
            </div>

            <div className="bg-slate-900/80 p-5 rounded-xl border border-slate-800/80 shadow-xs hover:border-slate-700 transition-colors">
              <div className="text-sky-400 text-xl mb-2.5">
                <i className="fa-solid fa-camera" aria-hidden="true" />
              </div>
              <h3 className="text-sm font-bold text-white mb-1">Billedtagning & Fotos</h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                Autentiske billeder af behandlere og faciliteter, der afmystificerer klinikken og fjerner barrierer.
              </p>
            </div>

            <div className="bg-slate-900/80 p-5 rounded-xl border border-slate-800/80 shadow-xs hover:border-slate-700 transition-colors">
              <div className="text-sky-400 text-xl mb-2.5">
                <i className="fa-solid fa-scissors" aria-hidden="true" />
              </div>
              <h3 className="text-sm font-bold text-white mb-1">Shortform Redigering</h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                Skarpe fængende klip med høj fastholdelse, der forvandler seere i feedet til motiverede patienter.
              </p>
            </div>

            <div className="bg-slate-900/80 p-5 rounded-xl border border-slate-800/80 shadow-xs hover:border-slate-700 transition-colors">
              <div className="text-sky-400 text-xl mb-2.5">
                <i className="fa-solid fa-closed-captioning" aria-hidden="true" />
              </div>
              <h3 className="text-sm font-bold text-white mb-1">Undertekster & Grafik</h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                Tydelig formidling og klinikprofilering, så budskabet altid forstås og huskes – også uden lyd.
              </p>
            </div>

            <div className="bg-slate-900/80 p-5 rounded-xl border border-slate-800/80 shadow-xs hover:border-slate-700 transition-colors">
              <div className="text-sky-400 text-xl mb-2.5">
                <i className="fa-solid fa-calendar-check" aria-hidden="true" />
              </div>
              <h3 className="text-sm font-bold text-white mb-1">Meta Postkalender</h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                Fast rytme og kontinuitet, der sikrer, at jeres praksis altid er synlig og top-of-mind i lokalområdet.
              </p>
            </div>

            <div className="bg-slate-900/80 p-5 rounded-xl border border-slate-800/80 shadow-xs hover:border-slate-700 transition-colors">
              <div className="text-sky-400 text-xl mb-2.5">
                <i className="fa-solid fa-share-nodes" aria-hidden="true" />
              </div>
              <h3 className="text-sm font-bold text-white mb-1">SoMe-profilopsætning</h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                Professionelle profiler, der udstråler kvalitet og giver patienten tryghed ved første klik.
              </p>
            </div>

            <div className="bg-slate-900/80 p-5 rounded-xl border border-slate-800/80 shadow-xs hover:border-slate-700 transition-colors">
              <div className="text-sky-400 text-xl mb-2.5">
                <i className="fa-solid fa-lightbulb" aria-hidden="true" />
              </div>
              <h3 className="text-sm font-bold text-white mb-1">Emnesparring & Idébank</h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                Svar på patienternes hyppigste bekymringer og spørgsmål, som opbygger autoritet og tryghed.
              </p>
            </div>

            <div className="bg-slate-900/80 p-5 rounded-xl border border-slate-800/80 shadow-xs hover:border-slate-700 transition-colors">
              <div className="text-sky-400 text-xl mb-2.5">
                <i className="fa-solid fa-location-dot" aria-hidden="true" />
              </div>
              <h3 className="text-sm font-bold text-white mb-1">Lokal Synlighed</h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                Målrettet synlighed mod borgere i jeres geografi, der søger behandling i netop jeres område.
              </p>
            </div>
          </div>

          <div className="mt-8 pt-6 border-t border-slate-800 flex flex-wrap items-center justify-between gap-4">
            <span className="text-xs sm:text-sm text-slate-400">
              <i className="fa-solid fa-circle-info text-sky-400 mr-1.5" />
              Ingen faste prispakker – aftaler tilpasses individuelt efter hvad jeres praksis har brug for.
            </span>
            <Link
              to="/ydelser"
              className="inline-flex items-center gap-1.5 text-xs sm:text-sm font-bold text-sky-400 hover:text-sky-300"
            >
              <span>Se alle ydelser i detaljer</span>
              <i className="fa-solid fa-arrow-right text-[10px]" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>

      {/* HOW WE WORK / 4 STEP PROCESS */}
      <section className="py-16 sm:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-stretch">
            
            {/* Left side: Header & Process steps */}
            <div className="lg:col-span-6 space-y-8 flex flex-col justify-between">
              <div>
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                  Fra strategisk samtale til færdig produktion & synlighed
                </h2>
                <p className="text-base sm:text-lg text-slate-600 mt-3">
                  Vi ved, at behandlere har travlt. Vores proces er designet til at kræve et minimum af jeres tid.
                </p>
              </div>

              <div className="grid grid-cols-1 gap-4">
                <div className="relative flex items-start gap-4 p-5 rounded-2xl bg-white border border-slate-200/90 shadow-xs">
                  <span className="text-xl sm:text-2xl font-extrabold text-sky-600 shrink-0">01</span>
                  <div>
                    <h3 className="text-base font-bold text-slate-900 mb-1">Strategi & Emneplan</h3>
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                      Klar plan og udvalgte faglige emner baseret på patienternes hyppigste spørgsmål.
                    </p>
                  </div>
                </div>

                <div className="relative flex items-start gap-4 p-5 rounded-2xl bg-white border border-slate-200/90 shadow-xs">
                  <span className="text-xl sm:text-2xl font-extrabold text-sky-600 shrink-0">02</span>
                  <div>
                    <h3 className="text-base font-bold text-slate-900 mb-1">I optager & sender materialet</h3>
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                      I optager og tager billeder i klinikken, når det passer jer i hverdagen, og sender nemt råmaterialet til os.
                    </p>
                  </div>
                </div>

                <div className="relative flex items-start gap-4 p-5 rounded-2xl bg-white border border-slate-200/90 shadow-xs">
                  <span className="text-xl sm:text-2xl font-extrabold text-sky-600 shrink-0">03</span>
                  <div>
                    <h3 className="text-base font-bold text-slate-900 mb-1">Klip, grafik & efterbehandling</h3>
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                      Færdige videoer og formater med grafik, undertekster og jeres faglige visuelle profil.
                    </p>
                  </div>
                </div>

                <div className="relative flex items-start gap-4 p-5 rounded-2xl bg-white border border-slate-200/90 shadow-xs">
                  <span className="text-xl sm:text-2xl font-extrabold text-sky-600 shrink-0">04</span>
                  <div>
                    <h3 className="text-base font-bold text-slate-900 mb-1">Distribution & Vækst</h3>
                    <p className="text-xs sm:text-sm text-slate-600 leading-relaxed">
                      Målrettet udgivelse og optimering, der sikrer reel synlighed og kvalificerede patienter.
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Right side: Process image */}
            <div className="lg:col-span-6 h-full flex flex-col w-full">
              <div className="relative overflow-hidden h-full min-h-[380px] sm:min-h-[440px] lg:min-h-full flex flex-1 w-full">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=85,format=auto/assets/48b457c4-b6b2-4d62-b92b-6229aa267ddf/shortform-phone-filming.png"
                  alt="Online vejledning og smartphone-optagelse i sundhedsklinik"
                  className="w-full h-full min-h-[380px] sm:min-h-[440px] lg:min-h-full object-cover flex-1"
                  loading="lazy"
                />
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* CASES SHOWCASE (FROM CMS) */}
      <section className="py-16 sm:py-24 bg-white text-slate-900 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6">
            <div>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                Se hvordan vi skaber værdi i praksis
              </h2>
            </div>
            <Link
              to="/cases"
              className="inline-flex items-center gap-2 text-sky-700 hover:text-sky-800 font-semibold text-sm transition"
            >
              <span>Se alle cases</span>
              <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {cases.map((c) => {
              const imgUrl = c.featured_image?.[0] || '';
              const linkHref = `/cases/${c.identifier || c.slug?._ || c.id}`;
              return (
                <div 
                  key={c.id || c.identifier}
                  className="bg-white overflow-hidden border border-slate-200 hover:border-slate-300 transition flex flex-col"
                >
                  <div className="relative aspect-16/10 bg-slate-100 overflow-hidden">
                    {imgUrl && (
                      <img
                        src={getOptimizedImageUrl(imgUrl, { width: 600, height: 375, fit: 'cover' })}
                        alt={c.title}
                        className="w-full h-full object-cover"
                        loading="lazy"
                      />
                    )}
                  </div>

                  <div className="p-6 flex flex-col flex-1 justify-between">
                    <div>
                      <h3 className="text-xl font-bold text-slate-900 mb-2 line-clamp-2">
                        {c.title}
                      </h3>
                      <p className="text-slate-600 text-xs sm:text-sm leading-relaxed line-clamp-3 mb-4">
                        {c.summary}
                      </p>
                    </div>

                    <div className="pt-4 border-t border-slate-100 flex items-center justify-between">
                      <span className="text-xs text-slate-500 font-medium">
                        {c.format || 'Shortform format'}
                      </span>
                      <Link
                        to={linkHref}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-sky-700 hover:text-sky-800"
                      >
                        <span>Læs case</span>
                        <i className="fa-solid fa-chevron-right text-[10px]" aria-hidden="true" />
                      </Link>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </section>

      {/* FINAL CTA SECTION */}
      <section className="relative overflow-hidden py-20 sm:py-28 bg-slate-950 text-white text-center">
        <div className="absolute inset-0 z-0">
          <img
            src={getOptimizedImageUrl('https://assets.auxodev.dk/assets/48b457c4-b6b2-4d62-b92b-6229aa267ddf/health-mobile-clinic-production.png', { width: 1920, quality: 85 })}
            alt="Sundhedsformidling med smartphone i klinik"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-slate-950/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/90 via-transparent to-slate-950/70" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white mb-6">
            Gør din klinik mere synlig og skab stærk patienttillid
          </h2>
          <p className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto mb-8 leading-relaxed">
            Book en uforpligtende samtale, hvor vi gennemgår jeres nuværende synlighed og skitserer, hvordan professionel videoproduktion og målrettet formidling kan hjælpe jer.
          </p>
          <Link
            to="/kontakt"
            className="inline-flex items-center justify-center gap-3 px-8 py-4 rounded-full bg-sky-500 hover:bg-sky-400 text-slate-950 font-bold text-lg transition-all duration-200 shadow-xl shadow-sky-950/60 hover:-translate-y-0.5"
          >
            <span>Book en samtale</span>
            <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true" />
          </Link>
        </div>
      </section>

    </div>
  );
}
